/** @const {String[]} The sections we want returned from cart actions by default */
const DEFAULT_SECTIONS = []
/** @const {String} The context in which we want the sections to be rendered in */
const DEFAULT_SECTIONS_URL = window.location.pathname

/**
 * By Association Only's cart helper methods
 */
class BaoCart {
  /**
   * Add a new line item to the cart
   * @param {object} item The item we want to add values to pass to /cart/add.js
   * @param {number} item.id The variant's unique ID
   * @param {number} item.quantity The quantity of items to be added to the cart
   * @param {object} item.properties Line item property key/values
   *   (https://help.shopify.com/en/themes/liquid/objects/line_item#line_item-properties)
   * @param {HTMLFormElement} form The form that has added the item
   * @param {object} options Optional values to pass to /cart/add.js
   * @param {string[]} [options.sections] - The sections we want rendered alongside any cart
   *   changes
   * @param {string} [options.sectionsUrl] - The context in which we want the sections to be
   *   rendered in
   * @param {object} [options.attributes] Key/values that we want to add to the cart attributes
   *
   * @returns {Promise} Resolves with the line item object (See response of cart/add.js
   *   https://help.shopify.com/en/themes/development/getting-started/using-ajax-api#add-to-cart)
   */
  async addItem (
    { id, quantity = 1, properties = {} },
    form,
    {
      sections = DEFAULT_SECTIONS,
      sectionsUrl = DEFAULT_SECTIONS_URL,
      attributes = {},
      silent = false,
    }
  ) {
    const item = {
      id,
      quantity,
      properties,
    }

    if (!silent) {
      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.BEFORE_ADD, {
        item,
        attributes,
        form,
      })
    }

    const body = {
      items: [item],
      attributes,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      let state = await this.cartAdd(body)
      console.info('addItem state:', state)

      if (!silent) {
        state = await this.processGiftWithPurchase(state)
        console.info('addItem state:', state)

        window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.AFTER_ADD, {
          item,
          state,
          form,
        })
      }

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * Updates the quantity of an item in the cart. You can set the quantity to 0 to remove the item
   *
   * @param {number} line - The index of the line item we want to change
   * @param {number} quantity - The amount of the item we want change the line item to. Can be 0.
   * @param {string[]} [sections] - The sections we want rendered alongside any cart changes
   * @param {string} [sectionsUrl] - The context in which we want the sections to be rendered in
   *
   * @returns {Promise<object>}
   */
  async updateQuantity (
    line,
    quantity,
    sections = DEFAULT_SECTIONS,
    sectionsUrl = DEFAULT_SECTIONS_URL
  ) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_ITEM_QUANTITY,
      {
        line,
        quantity,
      }
    )
    const body = {
      line,
      quantity,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      let state = await this.cartChange(body)
      console.info('updateQuantity state:', state)
      state = await this.processGiftWithPurchase(state)
      console.info('updateQuantity state:', state)

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_ITEM_QUANTITY,
        {
          state,
        }
      )

      return state
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {BaoCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async removeItemByKey (
    key,
    { sections = [], sectionsUrl = DEFAULT_SECTIONS_URL, silent = false } = {}
  ) {
    if (!silent) {
      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_REMOVE_ITEM,
        { key }
      )
    }

    const body = {
      id: key,
      quantity: 0,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      let state = await this.cartChange(body)
      console.info('removeItem state:', state)

      if (!silent) {
        state = await this.processGiftWithPurchase(state)

        window.BAO.dispatchEvent(
          window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
          {
            state,
          }
        )

        window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.CHANGED, {
          state,
        })
      }

      return state
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Remove an item from the cart
   *
   * @param {number} line - The index of the line item we want to change
   * @param {string[]} [sections] - The sections we want rendered alongside any cart changes
   * @param {string} [sectionsUrl] - The context in which we want the sections to be rendered in
   *
   * @returns {Promise}
   */
  async removeItem (
    line,
    sections = [],
    sectionsUrl = window.location.pathname
  ) {
    return this.updateQuantity(line, 0, sections, sectionsUrl)
  }

  /**
   * Update cart attributes
   *
   * https://shopify.dev/api/ajax/reference/cart#update-cart-attributes
   *
   * @param {object} attributes - An object of key/value pairs you want to update
   *
   * @returns {Promise}
   **/
  async updateCartAttributes (attributes) {
    window.BAO.dispatchEvent(
      window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_CART_ATTRIBUTES,
      {
        attributes,
      }
    )

    try {
      const state = await window.BAO.utils.fetchJSON(
        `${window.routes.cartUpdateUrl}`,
        {
          ...window.BAO.utils.getDefaultRequestConfig(),
          ...{ body: JSON.stringify({ attributes }) },
        }
      )

      window.BAO.dispatchEvent(
        window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_CART_ATTRIBUTES,
        {
          attributes,
        }
      )

      return state
    } catch (e) {
      console.error(e)
    }
  }

  async processGiftWithPurchase (
    state,
    { silent = true, noRefresh = false } = {}
  ) {
    const giftWithPurchase = window.BAO.GiftWithPurchase
    if (!giftWithPurchase.isEligible()) {
      console.info(
        'GWP - No eligible elements, skipping processGiftWithPurchase'
      )
      return state
    }
    const {
      productsToAdd,
      productsToRemove,
    } = await giftWithPurchase.getProductsToAddRemove()
    console.info('processGiftWithPurchase:', {
      productsToAdd,
      productsToRemove,
    })

    if (productsToAdd?.length === 0 && productsToRemove?.length === 0) {
      if (!noRefresh) return state
      return null
    }

    const sections = window.BAO.utils.sectionRenderer.pushCartSections.map(
      s => s.section
    )
    const sectionsUrl = DEFAULT_SECTIONS_URL

    console.info('GWP - Removing products:', productsToRemove)
    for (const product of productsToRemove) {
      console.info('GWP - Removing product:', product)
      state = await this.removeItemByKey(product.key, {
        sections,
        sectionsUrl,
        silent,
      })
    }

    console.info('GWP - Adding products:', productsToAdd)
    for (const product of productsToAdd) {
      console.info('GWP - Adding product:', product)
      state = await this.addItem(product, null, {
        sections,
        sectionsUrl,
        silent,
      })
    }

    return state
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartChange (body) {
    return window.BAO.utils.fetchJSON(`${window.routes.cartChangeUrl}`, {
      ...window.BAO.utils.getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartAdd (body) {
    return window.BAO.utils.fetchJSON(`${window.routes.cartAddUrl}`, {
      ...window.BAO.utils.getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  async getCart () {
    return window.BAO.utils.fetchJSON('/cart.js', {
      ...window.BAO.utils.getDefaultRequestConfig(),
    })
  }
}

window.BAO.Cart = new BaoCart()

// Call processGiftWithPurchase on page load with silent: false
;(async () => {
  const state = await window.BAO.Cart.getCart()
  await window.BAO.Cart.processGiftWithPurchase(state, {
    silent: false,
    noRefresh: true,
  })
})()
